import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Car, Loader2, Clock, TrendingDown, Zap, ArrowRight, Heart, Navigation2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const POPULAR_ROUTES = [
  { pickup: "Koramangala, Bangalore", drop: "Whitefield, Bangalore" },
  { pickup: "MG Road, Bangalore", drop: "Electronic City, Bangalore" },
  { pickup: "Indiranagar, Bangalore", drop: "Airport, Bangalore" },
  { pickup: "Connaught Place, Delhi", drop: "IGI Airport, Delhi" },
  { pickup: "Bandra, Mumbai", drop: "Andheri, Mumbai" },
];

const RIDE_ICONS = {
  "Auto": "🛺", "Mini": "🚗", "Prime Sedan": "🚘", "Prime SUV": "🚙", "Bike": "🏍️",
  "UberGo": "🚗", "Premier": "🚘", "UberXL": "🚙", "Moto": "🏍️",
};

const RidePriceCompare = () => {
  const [pickupQuery, setPickupQuery] = useState('');
  const [dropQuery, setDropQuery] = useState('');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropSuggestions, setDropSuggestions] = useState([]);
  const [showPickupSugs, setShowPickupSugs] = useState(false);
  const [showDropSugs, setShowDropSugs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [pickupLoading, setPickupLoading] = useState(false);
  const [dropLoading, setDropLoading] = useState(false);
  const pickupDebounce = useRef(null);
  const dropDebounce = useRef(null);
  const pickupRef = useRef(null);
  const dropRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (pickupRef.current && !pickupRef.current.contains(e.target)) setShowPickupSugs(false);
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropSugs(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = async (query, setter, setLoadingState, biasCoords = null) => {
    if (query.length < 2) { setter([]); return; }
    setLoadingState(true);
    try {
      let url = `${API}/ride-compare/geocode?query=${encodeURIComponent(query)}`;
      if (biasCoords) {
        url += `&lat=${biasCoords.lat}&lng=${biasCoords.lng}`;
      }
      const r = await axios.get(url);
      setter(r.data.results || []);
    } catch { setter([]); }
    finally { setLoadingState(false); }
  };

  const handlePickupChange = (e) => {
    const val = e.target.value;
    setPickupQuery(val);
    setPickupCoords(null);
    clearTimeout(pickupDebounce.current);
    pickupDebounce.current = setTimeout(() => fetchSuggestions(val, setPickupSuggestions, setPickupLoading), 300);
    setShowPickupSugs(true);
  };

  const handleDropChange = (e) => {
    const val = e.target.value;
    setDropQuery(val);
    setDropCoords(null);
    clearTimeout(dropDebounce.current);
    // Bias drop suggestions toward pickup location for relevance (Ola/Uber-style)
    dropDebounce.current = setTimeout(() => fetchSuggestions(val, setDropSuggestions, setDropLoading, pickupCoords), 300);
    setShowDropSugs(true);
  };

  const selectPickup = (item) => {
    setPickupQuery(item.name);
    setPickupCoords({ lat: item.lat, lng: item.lng });
    setShowPickupSugs(false);
    setPickupSuggestions([]);
    // Auto-focus drop input after selecting pickup
    setTimeout(() => document.querySelector('[data-testid="drop-input"]')?.focus(), 100);
  };

  const selectDrop = (item) => {
    setDropQuery(item.name);
    setDropCoords({ lat: item.lat, lng: item.lng });
    setShowDropSugs(false);
    setDropSuggestions([]);
  };

  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          setPickupCoords({ lat: latitude, lng: longitude });
          // Reverse geocode to get name
          try {
            const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
            const data = await r.json();
            const addr = data.address || {};
            const name = addr.road || addr.suburb || addr.neighbourhood || 'Current Location';
            const area = addr.suburb || addr.city_district || addr.city || '';
            setPickupQuery(area ? `${name}, ${area}` : name);
          } catch {
            setPickupQuery('Current Location');
          }
          toast.success('Location detected');
          setTimeout(() => document.querySelector('[data-testid="drop-input"]')?.focus(), 100);
        },
        () => toast.error('Could not get location'),
        { timeout: 10000, enableHighAccuracy: true }
      );
    }
  };

  const searchRides = async (pickup, drop) => {
    const pq = pickup || pickupQuery;
    const dq = drop || dropQuery;

    let pCoords = pickupCoords;
    let dCoords = dropCoords;

    // If using a popular route, geocode both
    if (pickup && drop) {
      setPickupQuery(pickup);
      setDropQuery(drop);
      try {
        const [pRes, dRes] = await Promise.all([
          axios.get(`${API}/ride-compare/geocode?query=${encodeURIComponent(pickup)}`),
          axios.get(`${API}/ride-compare/geocode?query=${encodeURIComponent(drop)}`),
        ]);
        if (pRes.data.results?.[0] && dRes.data.results?.[0]) {
          pCoords = { lat: pRes.data.results[0].lat, lng: pRes.data.results[0].lng };
          dCoords = { lat: dRes.data.results[0].lat, lng: dRes.data.results[0].lng };
          setPickupCoords(pCoords);
          setDropCoords(dCoords);
        } else {
          toast.error('Could not find locations'); return;
        }
      } catch { toast.error('Geocoding failed'); return; }
    }

    if (!pCoords || !dCoords) { toast.error('Select both pickup and drop locations'); return; }

    setLoading(true); setResults(null);
    try {
      const r = await axios.post(`${API}/ride-compare/search`, {
        pickup_lat: pCoords.lat, pickup_lng: pCoords.lng,
        drop_lat: dCoords.lat, drop_lng: dCoords.lng,
        pickup_name: pq, drop_name: dq,
      });
      setResults(r.data);
      toast.success(`Found ${(r.data.ola_rides?.length || 0) + (r.data.uber_rides?.length || 0)} ride options!`);
    } catch (err) { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  const cheapest = results?.cheapest;

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="ride-compare-page">
      <Toaster position="top-right" richColors theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#09090b]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/dashboard" className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center hover:scale-110 transition-transform" data-testid="back-to-healthai"><Heart className="w-4 h-4 text-white" /></a>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><Car className="w-4 h-4" /></div>
            <div><h1 className="text-base font-bold tracking-tight">RideCompare AI</h1><p className="text-[10px] text-zinc-500">Ola vs Uber. Same route. Best fare.</p></div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/food-compare" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">Food Compare</a>
            <a href="/dashboard" className="text-xs text-zinc-500 hover:text-white transition hidden sm:block">HealthAI</a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Compare ride fares</h2>
          <p className="text-zinc-500 text-sm mb-6">Realistic Ola & Uber fare estimates using city rate cards + live surge</p>
        </div>

        {/* Route Input */}
        <div className="max-w-lg mx-auto mb-8 space-y-2" data-testid="route-form">
          {/* Pickup */}
          <div className="relative" ref={pickupRef}>
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-green-300" />
            <Input value={pickupQuery} onChange={handlePickupChange} onFocus={() => { if (pickupSuggestions.length) setShowPickupSugs(true); }} placeholder="Enter pickup location" className="pl-8 pr-10 h-12 bg-zinc-900/80 border-zinc-800 text-white rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500" data-testid="pickup-input" />
            <button onClick={useCurrentLocation} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-zinc-800 rounded-lg transition" title="Use current location"><Navigation2 className="w-4 h-4 text-violet-400" /></button>
            {pickupLoading && <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-500" />}
            {showPickupSugs && pickupSuggestions.length > 0 && (
              <div className="absolute z-30 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-h-56 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200">
                {pickupSuggestions.map((s, i) => (
                  <button key={`pu-${i}`} onClick={() => selectPickup(s)} className="w-full text-left px-4 py-3 text-sm hover:bg-zinc-800/80 transition flex items-start gap-3 border-b border-zinc-800/30 last:border-0">
                    <MapPin className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{s.name}</p>
                      {s.full_name && s.full_name !== s.name && <p className="text-[11px] text-zinc-500 truncate mt-0.5">{s.full_name}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Connector line */}
          <div className="flex items-center pl-[18px]"><div className="w-0.5 h-4 bg-zinc-700 rounded" /></div>

          {/* Drop */}
          <div className="relative" ref={dropRef}>
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 bg-red-500 rounded-sm" />
            <Input value={dropQuery} onChange={handleDropChange} onFocus={() => { if (dropSuggestions.length) setShowDropSugs(true); }} placeholder="Where to?" className="pl-8 h-12 bg-zinc-900/80 border-zinc-800 text-white rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500" data-testid="drop-input" />
            {dropLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-zinc-500" />}
            {showDropSugs && dropSuggestions.length > 0 && (
              <div className="absolute z-30 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-h-56 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-200">
                {dropSuggestions.map((s, i) => (
                  <button key={`dr-${i}`} onClick={() => selectDrop(s)} className="w-full text-left px-4 py-3 text-sm hover:bg-zinc-800/80 transition flex items-start gap-3 border-b border-zinc-800/30 last:border-0">
                    <MapPin className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{s.name}</p>
                      {s.full_name && s.full_name !== s.name && <p className="text-[11px] text-zinc-500 truncate mt-0.5">{s.full_name}</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button onClick={() => searchRides()} disabled={loading || !pickupCoords || !dropCoords} className="w-full h-11 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 font-semibold mt-3" data-testid="compare-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" />Compare Rides</>}
          </Button>
        </div>

        {/* Popular Routes */}
        {!results && !loading && (
          <div className="mb-10" data-testid="popular-routes">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mb-3">Popular routes</p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_ROUTES.map((route, i) => (
                <button key={`route-${i}`} onClick={() => searchRides(route.pickup, route.drop)} className="px-3 py-2 rounded-xl bg-zinc-900 border border-zinc-800 text-xs text-zinc-400 hover:text-white hover:border-zinc-600 transition text-left">
                  <span className="text-green-400">{route.pickup.split(',')[0]}</span>
                  <ArrowRight className="w-3 h-3 inline mx-1 text-zinc-600" />
                  <span className="text-red-400">{route.drop.split(',')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-2xl px-6 py-4">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              <div className="text-left"><p className="font-medium text-sm">Estimating fares...</p><p className="text-xs text-zinc-500">Calculating route, rate cards & surge</p></div>
            </div>
          </div>
        )}

        {/* Results */}
        {results && !loading && (
          <div className="space-y-6" data-testid="results-section">
            {/* Route Info */}
            <div className="flex items-center justify-center gap-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-green-400" />{results.pickup_name?.split(',')[0]}</span>
              <ArrowRight className="w-4 h-4 text-zinc-600" />
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-red-400" />{results.drop_name?.split(',')[0]}</span>
              <Badge className="bg-zinc-800 text-zinc-400">{results.distance_km} km</Badge>
              <Badge className="bg-zinc-800 text-zinc-400"><Clock className="w-3 h-3 mr-1" />{results.duration_min} min</Badge>
            </div>

            {/* Cheapest Banner */}
            {cheapest && (
              <Card className="bg-gradient-to-r from-green-950/40 to-emerald-950/40 border-green-800/20" data-testid="cheapest-ride">
                <CardContent className="p-5 flex items-center gap-5">
                  <div className="w-12 h-12 rounded-2xl bg-green-500/20 flex items-center justify-center shrink-0"><TrendingDown className="w-6 h-6 text-green-400" /></div>
                  <div className="flex-1">
                    <p className="text-xs text-green-400 font-medium mb-0.5">Cheapest Car</p>
                    <h3 className="text-lg font-bold">{RIDE_ICONS[cheapest.ride_type]} {cheapest.ride_type}</h3>
                    <p className="text-xs text-zinc-400">via <span className={cheapest.platform === 'ola' ? 'text-green-400' : 'text-zinc-200'}>{cheapest.platform === 'ola' ? 'Ola' : 'Uber'}</span> {cheapest.eta && `| ETA ${cheapest.eta}`}</p>
                  </div>
                  <div className="text-right"><p className="text-3xl font-bold text-green-400">₹{cheapest.price}</p><p className="text-xs text-zinc-500">{cheapest.price_range}</p></div>
                </CardContent>
              </Card>
            )}

            {/* Side by Side Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ola */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center"><span className="text-green-400 font-bold text-sm">O</span></div>
                  <h3 className="font-semibold text-green-400">Ola</h3>
                </div>
                <div className="space-y-2">
                  {results.ola_rides?.map((ride, idx) => (
                    <RideCard key={`ola-${idx}`} ride={ride} isCheapest={cheapest?.platform === 'ola' && cheapest?.ride_type === ride.ride_type} />
                  ))}
                </div>
              </div>

              {/* Uber */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center"><span className="text-zinc-200 font-bold text-sm">U</span></div>
                  <h3 className="font-semibold text-zinc-200">Uber</h3>
                </div>
                <div className="space-y-2">
                  {results.uber_rides?.map((ride, idx) => (
                    <RideCard key={`uber-${idx}`} ride={ride} isCheapest={cheapest?.platform === 'uber' && cheapest?.ride_type === ride.ride_type} />
                  ))}
                </div>
              </div>
            </div>

            {/* All rides sorted by price */}
            <div>
              <h3 className="text-sm font-semibold mb-3 text-zinc-400">All options (cars first, cheapest)</h3>
              <div className="space-y-1.5">
                {(() => {
                  const CAR_TYPES = new Set(['Mini', 'Prime Sedan', 'Prime SUV', 'UberGo', 'Premier', 'UberXL']);
                  const AUTO_TYPES = new Set(['Auto']);
                  const rank = (rt) => CAR_TYPES.has(rt) ? 0 : (AUTO_TYPES.has(rt) ? 1 : 2);
                  return [...(results.ola_rides || []), ...(results.uber_rides || [])]
                    .sort((a, b) => rank(a.ride_type) - rank(b.ride_type) || a.price - b.price)
                    .map((ride, idx) => (
                  <div key={`all-${idx}`} className={`flex items-center gap-3 p-3 rounded-xl border ${idx === 0 ? 'bg-green-950/20 border-green-800/20' : 'bg-zinc-900/40 border-zinc-800/50'}`} data-testid={`ride-row-${idx}`}>
                    <span className="text-lg w-8">{RIDE_ICONS[ride.ride_type]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{ride.ride_type}</p>
                      <p className="text-[10px] text-zinc-500">{ride.platform === 'ola' ? 'Ola' : 'Uber'} {ride.surge > 1 ? `| ${ride.surge}x surge` : ''}</p>
                    </div>
                    {ride.eta && <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Clock className="w-3 h-3" />{ride.eta}</span>}
                    <Badge className={`text-xs ${ride.platform === 'ola' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700/50 text-zinc-300'}`}>{ride.platform}</Badge>
                    <div className="text-right min-w-[70px]">
                      <p className="text-lg font-bold">₹{ride.price}</p>
                      <p className="text-[9px] text-zinc-600">{ride.price_range}</p>
                    </div>
                  </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-zinc-800/50 mt-12">
        <div className="max-w-5xl mx-auto px-4 py-4 text-center text-[11px] text-zinc-700">
          Estimates use official city rate cards + time-based surge (peak/night). Actual fares may vary with live surge and promotions.
        </div>
      </footer>
    </div>
  );
};

const RideCard = ({ ride, isCheapest }) => (
  <Card className={`overflow-hidden ${isCheapest ? 'border-green-800/30 bg-green-950/10' : 'bg-zinc-900/50 border-zinc-800/50'}`} data-testid="ride-card">
    <CardContent className="p-3 flex items-center gap-3">
      <span className="text-2xl w-10 text-center">{RIDE_ICONS[ride.ride_type]}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-sm">{ride.ride_type}</h4>
          {isCheapest && <Badge className="bg-green-500/20 text-green-400 text-[9px]">CHEAPEST</Badge>}
          {ride.surge > 1 && <Badge className="bg-red-500/20 text-red-400 text-[9px]">{ride.surge}x surge</Badge>}
        </div>
        <p className="text-[10px] text-zinc-500">{ride.eta} away | {ride.distance_km} km | {ride.duration_min} min</p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-xl font-bold">₹{ride.price}</p>
        <p className="text-[9px] text-zinc-500">{ride.price_range}</p>
      </div>
    </CardContent>
  </Card>
);

export default RidePriceCompare;
